export const LOGO_URL = "https://customer-assets.emergentagent.com/job_f3d78c41-ae57-41ba-b44a-0377766e8766/artifacts/6bt4gwgo_LOGO%20ENERGI.png";

// 2026-06-14 (user request): label status mengacu glosarium terpusat
// `lib/i18nLabels.js` — ubah wording cukup di sana.
import { TASK_STATUS_LABEL, PRIORITY_LABEL_ID, PROJECT_STATUS_LABEL, COMMON_STATUS_LABEL } from "@/lib/i18nLabels";

export const STATUS_LABEL = TASK_STATUS_LABEL;

export const STATUS_COLOR = {
  todo: "bg-slate-100 text-slate-700 border-slate-300",
  in_progress: "bg-blue-50 text-blue-700 border-blue-300",
  menunggu_verifikasi: "bg-amber-50 text-amber-800 border-amber-300",
  selesai: "bg-emerald-50 text-emerald-700 border-emerald-300",
  ditolak: "bg-yellow-50 text-yellow-800 border-yellow-400",
  dibatalkan: "bg-slate-200 text-slate-600 border-slate-400 line-through",
};

export const PRIORITY_LABEL = PRIORITY_LABEL_ID;

export const PRIORITY_COLOR = {
  Rendah: "bg-slate-100 text-slate-700",
  Sedang: "bg-blue-50 text-blue-700",
  Tinggi: "bg-orange-50 text-orange-700",
  Kritis: "bg-rose-50 text-rose-700",
};

export const DIVISION_COLOR = {
  BOD: "bg-[#0B1D3A] text-white",
  FAT: "bg-blue-600 text-white",
  HRGA: "bg-teal-600 text-white",
  "Procurement Asset Logistic": "bg-purple-600 text-white",
  Project: "bg-[#FF5A1F] text-white",
};

export const DIVISION_ORDER = { BOD: 0, FAT: 1, HRGA: 2, "Procurement Asset Logistic": 3, Project: 4 };

export const DIVISIONS = ["BOD", "FAT", "HRGA", "Procurement Asset Logistic", "Project"];

export const PROJECT_STATUS = {
  active: { label: PROJECT_STATUS_LABEL.active, cls: "bg-white border-slate-300 text-slate-700" },
  // 2026-06-20 (user request): maintenance (is_outlet) yang semua WO-nya closed → Standby.
  standby: { label: PROJECT_STATUS_LABEL.standby, cls: "bg-slate-100 border-slate-300 text-slate-600" },
  overdate: { label: PROJECT_STATUS_LABEL.overdate, cls: "bg-white border-slate-400 text-slate-800 font-bold" },
  close: { label: PROJECT_STATUS_LABEL.close, cls: "bg-white border-slate-300 text-slate-500" },
  // 149-bp: Cancelled by Owner — status terminal terpisah dari Close.
  cancelled_by_owner: { label: PROJECT_STATUS_LABEL.cancelled_by_owner, cls: "bg-slate-100 border-slate-400 text-slate-600 line-through" },
  // Legacy statuses fallback to display purposes (still stored in DB but shown as one of the 3)
  awaiting_payment: { label: PROJECT_STATUS_LABEL.awaiting_payment, cls: "bg-white border-slate-300 text-slate-500" },
  completed: { label: PROJECT_STATUS_LABEL.completed, cls: "bg-white border-slate-300 text-slate-500" },
  cancelled: { label: PROJECT_STATUS_LABEL.cancelled, cls: "bg-white border-slate-300 text-slate-500" },
};

// Derive the simplified status (Active / Overdate / Close / Cancelled by Owner) from raw DB status
// + contract end_date. Cancelled by Owner tetap terpisah supaya KPI tidak menghitung sebagai selesai.
export function deriveProjectStatus(p) {
  if (!p) return "active";
  const raw = (p.status || "active").toLowerCase();
  if (raw === "cancelled_by_owner") return "cancelled_by_owner";
  if (raw === "standby") return "standby";
  if (["completed", "awaiting_payment", "cancelled", "close"].includes(raw)) return "close";
  const endRaw = p.project_type === "variation_order" ? p.vo_end_date : p.end_date;
  if (endRaw) {
    const end = new Date(endRaw + "T23:59:59");
    if (!isNaN(end.getTime()) && new Date() > end) return "overdate";
  }
  return "active";
}

// 149-cw-r111 (2026-08-28) — Hapus hardcode threshold DPP 5jt.
// isSplit sekarang murni ditentukan oleh `retention_pct` WO:
//   retention_pct > 0 → invoice split (95% + 5% retensi)
//   retention_pct = 0 → invoice full 100% (tanpa retensi)
// Keputusan retensi ditentukan user saat set WO/kontrak, bukan otomatis oleh
// nilai kontrak. dppThreshold dipertahankan sebagai default fallback tapi
// tidak lagi menentukan isSplit.
export const DEFAULT_DPP_THRESHOLD_IDR = 5_000_000;
export const DEFAULT_PPN_RATE = 0.11;

export function getWOInvoicePolicy(w, opts) {
  const dppThreshold = (opts && opts.dppThreshold != null) ? opts.dppThreshold : DEFAULT_DPP_THRESHOLD_IDR;
  const ppnRate = (opts && opts.ppnRate != null) ? opts.ppnRate : DEFAULT_PPN_RATE;
  const contractValue = Number((w && w.contract_value) || 0);
  const dppEstimate = contractValue / (1 + ppnRate);
  const retentionPct = Number((w && w.retention_pct) || 0);
  const isSplit = retentionPct > 0;
  return { isSplit, dppEstimate, dppThreshold, ppnRate, retentionPct };
}

// 16-WO-SPH-PO-WORKFLOW-R1 (2026-02-28): Alur WO diringkas.
//   WO Diterima → Site Survey → SPH & Negosiasi → Penerbitan PO → Pengerjaan → Selesai Fisik → Invoice → Closed
// 149-cw-r94 (2026-08-27): SPH & Negosiasi vs Penerbitan PO dipisah menjadi 2
//   stage terpisah supaya funnel monitoring lebih akurat.
// - `sph_negosiasi` = SPH sudah dikirim namun latest sub-status BUKAN approved
//   (masih sph_sent / sph_negotiation) & PO belum terbit.
// - `penerbitan_po` = SPH latest sub-status = approved (Verifikasi — Menunggu
//   PO) & PO belum terbit. Ketika `po_number` diisi → transisi ke `pengerjaan`.
// - Legacy `sph_negosiasi_po` & `menunggu_po` tetap ada di WO_COMBINED_META
//   supaya data lama tetap terbaca; TIDAK ada mutasi DB.
// 149-cw-r110 (2026-08-28) — Simplified color palette: satu warna utama navy
// untuk seluruh funnel; badge memakai warna muda; semantic colors hanya untuk
// kuning=menunggu, merah=urgent/overdue, hijau=Closed, abu-abu=Dibatalkan.
export const WO_STAGES = [
  { key: "wo_diterima",         label: "WO Diterima",          badge: "bg-slate-50 text-slate-700 border-slate-300" },
  { key: "site_survey",         label: "Site Survey",          badge: "bg-slate-50 text-slate-700 border-slate-300" },
  { key: "sph_negosiasi",       label: "SPH & Negosiasi",      badge: "bg-amber-50 text-amber-800 border-amber-200" },
  { key: "penerbitan_po",       label: "Penerbitan PO",        badge: "bg-amber-50 text-amber-800 border-amber-200" },
  { key: "pengerjaan",          label: "Pengerjaan",           badge: "bg-amber-50 text-amber-800 border-amber-200" },
  { key: "selesai_fisik",       label: "Selesai Fisik",        badge: "bg-slate-50 text-slate-700 border-slate-300" },
  { key: "pembuatan_invoice",   label: "Pembuatan Invoice",    badge: "bg-amber-50 text-amber-800 border-amber-200" },
  { key: "invoice_full",        label: "Pengiriman Invoice 100%", badge: "bg-slate-50 text-[#0B1D3A] border-slate-300" },
  { key: "invoice_95",          label: "Pengiriman Invoice 95%",  badge: "bg-slate-50 text-[#0B1D3A] border-slate-300" },
  { key: "invoice_5_retention", label: "Invoice 5% (Retensi)", badge: "bg-slate-50 text-[#0B1D3A] border-slate-300" },
  { key: "closed",              label: "Closed",               badge: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  { key: "cancelled_by_owner",  label: "Dibatalkan Owner",     badge: "bg-slate-100 text-slate-500 border-slate-300 line-through" },
];

// 16-WO-SPH-PO-WORKFLOW-R1: Sub-status untuk tahap sph_negosiasi_po.
export const SPH_SUB_STATUS = {
  sph_not_sent:          { label: "SPH Belum Dikirim",           cls: "bg-slate-100 text-slate-700 border-slate-300" },
  sph_sent:              { label: "SPH Dikirim",                 cls: "bg-blue-50 text-blue-700 border-blue-300" },
  sph_negotiation:       { label: "Negosiasi / Revisi SPH",      cls: "bg-amber-50 text-amber-800 border-amber-300" },
  sph_approved_wait_po:  { label: "Verifikasi — Menunggu PO", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  po_issued:             { label: "PO Terbit",                   cls: "bg-orange-50 text-orange-700 border-orange-300" },
};

// Turunkan sub-status berdasar `sph_submissions[]` + `po_number`.
// - Tidak mengubah field tersimpan; murni derived.
// - `sph_submissions` shape: `{ id, date, note (revision_note), offer_value?, status?, ... }`
// - `status` sub berkualitas: `sent | revised | approved`. Kalau kosong → treat as `sent`.
export function deriveSphSubStatus(w) {
  if (!w) return "sph_not_sent";
  if ((w.po_number || "").trim()) return "po_issued";
  const subs = Array.isArray(w.sph_submissions) ? w.sph_submissions.filter((r) => r && r.date) : [];
  if (subs.length === 0 && !w.sph_submit_date) return "sph_not_sent";
  if (subs.length === 0) return "sph_sent"; // legacy: hanya sph_submit_date
  // Sort by date ASC then created_at as tie-breaker; latest = last item.
  const sorted = [...subs].sort((a, b) => {
    const da = (a.date || "");
    const db = (b.date || "");
    if (da !== db) return da.localeCompare(db);
    return (a.created_at || "").localeCompare(b.created_at || "");
  });
  const latest = sorted[sorted.length - 1];
  const st = (latest?.status || "").toLowerCase();
  if (st === "approved") return "sph_approved_wait_po";
  if (st === "revised" || (latest?.note || "").trim()) return "sph_negotiation";
  return "sph_sent";
}

// 16-WO-LIFECYCLE-FINANCIAL-PARITY-R1 + INVOICE-SPLIT-R1:
// - Retention invoice (is_retention_claim + invoice_date) → invoice_5_retention.
// - Main invoice (non-retention + invoice_date) → invoice_95 (split) atau invoice_full (single).
// - Legacy `invoice_sent_date` treated sebagai bukti main invoice sent bila belum ada invoice_date entry.
export function deriveWOStage(w, opts) {
  if (!w) return "wo_diterima";
  if (w.status === "cancelled_by_owner") return "cancelled_by_owner";
  if (w.status === "completed") return "closed";
  const entries = Array.isArray(w.income_entries) ? w.income_entries : [];
  const hasRetentionInvoice = entries.some((e) => e && e.invoice_date && e.is_retention_claim);
  const hasMainInvoice = entries.some((e) => e && e.invoice_date && !e.is_retention_claim);
  const policy = getWOInvoicePolicy(w, opts);
  if (hasRetentionInvoice) return "invoice_5_retention";
  if (hasMainInvoice || w.invoice_sent_date) return policy.isSplit ? "invoice_95" : "invoice_full";
  if (w.project_done_date && (w.po_number || "").trim()) return "pembuatan_invoice";
  if (w.project_done_date) return "selesai_fisik";
  if ((w.po_number || "").trim()) return "pengerjaan";
  // 149-cw-r94 (2026-08-27): SPH/Negosiasi vs Penerbitan PO dipisah.
  //   - `penerbitan_po` = SPH latest status "approved" (sub-status
  //     sph_approved_wait_po) & PO belum terbit → tahap "Penerbitan PO".
  //   - `sph_negosiasi` = SPH sudah dikirim tapi belum approved (sph_sent /
  //     sph_negotiation) & PO belum terbit → tahap "SPH & Negosiasi".
  const hasSph = w.sph_submit_date || (Array.isArray(w.sph_submissions) && w.sph_submissions.length > 0);
  if (hasSph) {
    const sub = deriveSphSubStatus(w);
    if (sub === "sph_approved_wait_po") return "penerbitan_po";
    return "sph_negosiasi";
  }
  if (w.site_survey_date) return "site_survey";
  return "wo_diterima";
}

// 16-WO-LIFECYCLE-FINANCIAL-PARITY-R1: status keuangan terpisah — tidak
// mengambil `contract_value` sebagai denominator (spec: current cash due =
// total invoice tercatat). Verified received hanya menghitung entry dengan
// invoice_date + received date + tidak waiting; entry ambigu (tanpa invoice_date
// tapi ada received) di-flag NEEDS_REVIEW.
export const WO_FINANCIAL_META = {
  UNBILLED:     { label: "Belum Ditagih",       cls: "bg-slate-50 text-slate-600 border-slate-300" },
  WAITING:      { label: "Menunggu Pembayaran", cls: "bg-orange-50 text-orange-700 border-orange-300" },
  PARTIAL:      { label: "Dibayar Sebagian",    cls: "bg-yellow-50 text-yellow-800 border-yellow-300" },
  PAID:         { label: "Lunas",               cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  NEEDS_REVIEW: { label: "Perlu Verifikasi",    cls: "bg-amber-100 text-amber-800 border-amber-400" },
  CANCELLED:    { label: "Dibatalkan",          cls: "bg-slate-100 text-slate-500 border-slate-300 line-through" },
};

// 16-WO-PAYMENT-TAX-R1 (149-cw-r15): Cash-due formula. PPh yang dipotong client
// BUKAN piutang — pajak sudah disetor ke DJP atas nama vendor. Retensi juga
// TIDAK dianggap kekurangan pembayaran (di-track terpisah via retention_claim
// entries). Cash due = gross_invoice − pph_amount − retention_amount per
// invoice non-retensi. Bila field pph_amount / retention_amount belum ada di
// legacy entries, fallback = gross (backward-compat: menghasilkan angka lama).
export function computeWOCashDue(w) {
  if (!w) return { cashDue: 0, invoiceTotal: 0, verifiedReceived: 0, hasProvenPaid: false, hasAmbiguousReceived: false };
  const entries = Array.isArray(w.income_entries)
    ? w.income_entries.filter((e) => e && !e.is_retention_claim) : [];
  let invoiceTotal = 0, verifiedReceived = 0, cashDue = 0;
  let hasProvenPaid = false, hasAmbiguousReceived = false;
  for (const e of entries) {
    const dpp = Number(e.dpp_amount || e.amount || 0);
    const gross = Number(e.gross_invoice_amount || 0) || dpp;
    const pph = Number(e.pph_amount || 0);
    const retention = Number(e.retention_amount || 0);
    if (e.invoice_date) {
      invoiceTotal += gross;
      // Cash due per invoice = gross − pph − retention. Bila entry punya
      // `net_received_amount` explicit, gunakan itu (source of truth dari
      // billing engine); else derive dari komponen. Legacy tanpa field pajak
      // → cash due = gross (backward compat).
      const netFromEngine = Number(e.net_received_amount || 0);
      if (netFromEngine > 0) cashDue += netFromEngine;
      else if (pph > 0 || retention > 0) cashDue += Math.max(0, gross - pph - retention);
      else cashDue += gross;
    }
    if (e.date && !e.waiting_payment) {
      const rec = Number(e.net_received_amount || e.gross_invoice_amount || e.amount || 0);
      verifiedReceived += rec;
      if (e.invoice_date) hasProvenPaid = true;
      else hasAmbiguousReceived = true;
    }
  }
  return {
    cashDue: Math.round(cashDue * 100) / 100,
    invoiceTotal: Math.round(invoiceTotal * 100) / 100,
    verifiedReceived: Math.round(verifiedReceived * 100) / 100,
    hasProvenPaid,
    hasAmbiguousReceived,
  };
}

export function deriveWOFinancialStatus(w) {
  if (!w) return { key: "UNBILLED", ...WO_FINANCIAL_META.UNBILLED };
  if (w.status === "cancelled_by_owner") return { key: "CANCELLED", ...WO_FINANCIAL_META.CANCELLED };
  const { cashDue, invoiceTotal, verifiedReceived, hasProvenPaid, hasAmbiguousReceived } = computeWOCashDue(w);
  if (invoiceTotal <= 0 && !hasAmbiguousReceived) return { key: "UNBILLED", ...WO_FINANCIAL_META.UNBILLED };
  if (hasAmbiguousReceived && !hasProvenPaid) return { key: "NEEDS_REVIEW", ...WO_FINANCIAL_META.NEEDS_REVIEW };
  if (verifiedReceived <= 0) return { key: "WAITING", ...WO_FINANCIAL_META.WAITING };
  // Bandingkan received vs cash_due (bukan gross invoice). Toleransi ±100 rupiah untuk pembulatan sen.
  if (cashDue > 0 && verifiedReceived + 100 >= cashDue) return { key: "PAID", ...WO_FINANCIAL_META.PAID };
  return { key: "PARTIAL", ...WO_FINANCIAL_META.PARTIAL };
}

// 16-WO-LIFECYCLE-FINANCIAL-PARITY-R1 (unify): satu sumber untuk label combined
// stage yang dipakai Summary Project → Maintenance list. Menggabungkan operational
// stage (`deriveWOStage`) dgn financial status (`deriveWOFinancialStatus`).
// Prioritas: cancelled > payment (paid/partial/waiting) > needs_review > operational.
export const WO_COMBINED_META = {
  cancel_by_owner:     { label: "Cancel by Owner",      color: "bg-rose-50 text-rose-700 border-rose-300" },
  lunas:               { label: "Lunas",                color: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  dibayar_sebagian:    { label: "Dibayar Sebagian",     color: "bg-yellow-50 text-yellow-800 border-yellow-300" },
  menunggu_pembayaran: { label: "Menunggu Pembayaran",  color: "bg-orange-50 text-orange-700 border-orange-300" },
  needs_review:        { label: "Perlu Verifikasi",     color: "bg-amber-100 text-amber-800 border-amber-400" },
  menunggu_invoice:    { label: "Menunggu Invoice",     color: "bg-purple-50 text-purple-700 border-purple-300" },
  invoice_recorded:    { label: "Invoice Tercatat",     color: "bg-purple-50 text-purple-700 border-purple-300" },
  invoice_full:        { label: "Invoice 100%",         color: "bg-purple-50 text-purple-700 border-purple-300" },
  invoice_95:          { label: "Invoice 95%",          color: "bg-purple-50 text-purple-700 border-purple-300" },
  invoice_5_retention: { label: "Invoice 5% (Retensi)", color: "bg-sky-50 text-sky-700 border-sky-300" },
  selesai_fisik:       { label: "Selesai Fisik",        color: "bg-indigo-50 text-indigo-700 border-indigo-300" },
  pengerjaan:          { label: "Pengerjaan",           color: "bg-sky-50 text-sky-700 border-sky-300" },
  // 16-WO-SPH-PO-WORKFLOW-R1: `menunggu_po` legacy key tetap disini supaya audit
  // history atau data lama yg pernah expose key ini tidak crash saat diproses UI.
  // Label baru = "SPH / Negosiasi / PO" sesuai stage tergabung.
  sph_negosiasi_po:    { label: "SPH / Negosiasi / PO", color: "bg-amber-50 text-amber-700 border-amber-300" },
  // 149-cw-r94 (2026-08-27): SPH/Negosiasi vs Penerbitan PO dipisah.
  sph_negosiasi:       { label: "SPH & Negosiasi",     color: "bg-amber-50 text-amber-700 border-amber-300" },
  penerbitan_po:       { label: "Penerbitan PO",       color: "bg-lime-50 text-lime-700 border-lime-300" },
  site_survey:         { label: "Site Survey",          color: "bg-cyan-50 text-cyan-700 border-cyan-300" },
  menunggu_po:         { label: "SPH / Negosiasi / PO", color: "bg-amber-50 text-amber-700 border-amber-300" },
  wo_diterima:         { label: "WO Diterima",          color: "bg-slate-100 text-slate-700 border-slate-300" },
  closed:              { label: "Closed",               color: "bg-slate-100 text-slate-500 border-slate-300" },
};

export function deriveWoCombinedStage(w) {
  if (!w) return "wo_diterima";
  if (w.status === "cancelled_by_owner") return "cancel_by_owner";
  if (w.status === "completed") return "closed";
  const fin = deriveWOFinancialStatus(w);
  if (fin.key === "PAID") return "lunas";
  if (fin.key === "PARTIAL") return "dibayar_sebagian";
  if (fin.key === "WAITING") return "menunggu_pembayaran";
  if (fin.key === "NEEDS_REVIEW") return "needs_review";
  const op = deriveWOStage(w);
  // Map operational invoice stages directly ke combined key.
  if (op === "invoice_5_retention" || op === "invoice_95" || op === "invoice_full") return op;
  return op; // selesai_fisik | pengerjaan | menunggu_po | wo_diterima | closed
}

// 149-bp: risk color derivation berdasarkan tahap + overdue.
// Biru: perlu data · Kuning: menunggu · Oranye: pengerjaan · Merah: overdue · Hijau: selesai · Abu: cancelled
export function deriveWORisk(w) {
  if (!w) return { key: "info", label: "—", cls: "bg-slate-100 text-slate-600 border-slate-300" };
  if (w.status === "cancelled_by_owner") return { key: "cancelled", label: "Dibatalkan Owner", cls: "bg-slate-100 text-slate-500 border-slate-300 line-through" };
  const stage = deriveWOStage(w);
  const today = new Date();
  const endRaw = w.project_done_date || w.end_date;
  let overdue = false;
  if (endRaw && stage !== "closed" && !String(stage).startsWith("invoice")) {
    const end = new Date(String(endRaw).slice(0, 10) + "T23:59:59");
    if (!isNaN(end.getTime()) && today > end) overdue = true;
  }
  if (overdue) return { key: "critical", label: COMMON_STATUS_LABEL.overdue, cls: "bg-rose-50 text-rose-700 border-rose-300 font-semibold" };
  if (stage === "closed") return { key: "closed", label: "Selesai", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" };
  if (String(stage).startsWith("invoice")) return { key: "invoice", label: "Invoice", cls: "bg-sky-50 text-sky-700 border-sky-300" };
  if (stage === "selesai_fisik") return { key: "done", label: "Selesai Fisik", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" };
  if (stage === "pengerjaan") return { key: "wip", label: "Pengerjaan", cls: "bg-orange-50 text-orange-700 border-orange-300" };
  if (stage === "pembuatan_invoice") return { key: "wait", label: "Pembuatan Invoice", cls: "bg-teal-50 text-teal-700 border-teal-300" };
  if (stage === "penerbitan_po") return { key: "wait", label: "Penerbitan PO", cls: "bg-lime-50 text-lime-700 border-lime-300" };
  if (stage === "sph_negosiasi") return { key: "wait", label: "SPH & Negosiasi", cls: "bg-amber-50 text-amber-700 border-amber-300" };
  if (stage === "sph_negosiasi_po") return { key: "wait", label: "SPH / Negosiasi / PO", cls: "bg-amber-50 text-amber-700 border-amber-300" };
  if (stage === "site_survey") return { key: "survey", label: "Site Survey", cls: "bg-cyan-50 text-cyan-700 border-cyan-300" };
  return { key: "info", label: "Perlu Survey", cls: "bg-blue-50 text-blue-700 border-blue-300" };
}

export const CANCEL_REASON_LABELS = {
  budget_cancelled: "Anggaran dibatalkan",
  work_not_needed: "Pekerjaan tidak jadi diperlukan",
  sph_rejected: "SPH/harga tidak disetujui",
  reassigned_to_vendor: "Dialihkan kepada vendor lain",
  location_closed: "Lokasi atau proyek ditutup",
  duplicate_error: "Kesalahan/duplikasi WO",
  other: "Lainnya",
};

// 149-bu: Unique display label untuk project di combobox/picker.
// Format: "Parent Name › WO 60028947 — Judul Pekerjaan" untuk WO/VO,
// atau "Project Name" untuk parent. Menghindari beberapa pilihan dgn nama sama.
export function projectDisplayLabel(p) {
  if (!p) return "";
  const parent = p.parent_project_name || null;
  const isWO = p.project_type === "working_order";
  const isVO = p.project_type === "variation_order";
  if (isWO) {
    const num = p.wo_number ? `WO ${p.wo_number}` : "WO";
    const title = p.wo_title || p.project_name || "";
    return parent ? `${parent} › ${num}${title ? ` — ${title}` : ""}` : `${num}${title ? ` — ${title}` : ""}`;
  }
  if (isVO) {
    const num = p.vo_number ? `VO ${p.vo_number}` : "VO";
    const title = p.project_name || "";
    return parent ? `${parent} › ${num}${title ? ` — ${title}` : ""}` : `${num}${title ? ` — ${title}` : ""}`;
  }
  return p.project_name || p.id;
}

// 2026-10-13 (R170, user request): formatter bersama locale id-ID — tanggal
// ringkas "12 Okt 2026". Penyimpanan & timezone TIDAK berubah (presentasi saja).
export function fmtDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

export function fmtDateTime(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

// Tanggal lengkap: "Senin, 12 Oktober 2026".
export function fmtDateLong(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

export function fmtDuration(sec) {
  if (sec == null) return "-";
  if (sec < 60) return `${Math.round(sec)} detik`;
  if (sec < 3600) return `${Math.round(sec / 60)} menit`;
  const h = Math.floor(sec / 3600);
  if (h < 24) { const m = Math.round((sec % 3600) / 60); return m ? `${h} jam ${m} menit` : `${h} jam`; }
  const d = Math.floor(h / 24); const rh = h % 24;
  return rh ? `${d} hari ${rh} jam` : `${d} hari`;
}

// Relative time in Indonesian ("baru saja", "5 menit lalu", "2 jam lalu", "3 hari lalu", "23 Sep 2025").
// Falls back to a full date after 7 days.
export function fmtRelativeTime(iso) {
  if (!iso) return null;
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const s = Math.max(0, Math.round((now - then) / 1000));
    if (s < 60) return "baru saja";
    const m = Math.round(s / 60);
    if (m < 60) return `${m} menit lalu`;
    const h = Math.round(s / 3600);
    if (h < 24) return `${h} jam lalu`;
    const d = Math.round(s / 86400);
    if (d < 7) return `${d} hari lalu`;
    return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return null; }
}

export function formatIDR(n) {
  if (n == null || isNaN(n)) return "-";
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n);
}

/** Susun alasan ketidakpastian kelengkapan saldo dari data engine (konsisten di
 * Dashboard, Kas & Bank, Ledger, dan Rekonsiliasi). Jumlah/kondisi dari data
 * aktual — TIDAK hardcode semua ketidakpastian sebagai "entry legacy". */
export function buildKetidakpastianAlasan(eng) {
  const parts = [];
  const nKonflik = eng?.realisasi_verified_conflicts || 0;
  if (nKonflik > 0) parts.push(`terdapat ${nKonflik} item dengan status verifikasi yang belum selaras`);
  if (eng?.has_legacy) parts.push("nominal transfer historis belum diketahui");
  return parts;
}

/** Kalimat tooltip "Belum dapat dipastikan" dari data engine. */
export function buildKetidakpastianTooltip(eng) {
  const parts = buildKetidakpastianAlasan(eng);
  if (parts.length === 0) return "";
  return `Saldo belum dapat dipastikan karena ${parts.join(" dan ")}. Lihat rincian di Rekonsiliasi Kas Staf.`;
}

// 16-PROJECT-FINANCIAL-VISIBILITY-R1: helper display nominal yang menghormati
// akses. Bila `restricted=true` (dari flag `_financials_redacted` di project
// doc atau `financials_restricted` di payment-progress response), tampilkan
// "Akses dibatasi" alih-alih "Rp 0" (misleading). Bila value null/undefined
// dan tidak restricted, jatuhkan ke "-" (data belum diisi vs disembunyikan).
export function formatIDRScoped(n, restricted = false) {
  if (restricted) return "Akses dibatasi";
  if (n == null || isNaN(n)) return "-";
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n);
}

// 16-WO-PAYMENT-PROGRESS-R1: Helper konsisten untuk tampilan progress bar
// pembayaran WO (Working Order) di seluruh app (Maintenance table, panel detail,
// dashboard). Formula (single source of truth):
//   progress % = (verified_actual_received / nilai_wo) × 100
// Kondisi tampilan:
//   - Nilai WO ≤ 0 → status "empty" label "Nilai WO belum diisi" (abu-abu).
//   - pct null / undefined (loading atau tidak ada data) → "loading" label "—".
//   - pct > 100 → "needs_verify" label "Perlu Verifikasi" (amber) + bar visual
//     tetap di-cap 100% supaya tidak overflow. Persentase asli tetap tampil
//     kecil untuk audit.
//   - pct ≥ 100 (tepat) → "paid" label "100%" (emerald).
//   - pct ≥ 50   → "on_track" (sky).
//   - default     → "in_progress" (amber).
// Args:
//   pct: number | null | undefined  → nilai persentase dari backend (verified
//                                     actual received / contract × 100).
//   contractValue: number           → nilai WO / kontrak (untuk deteksi 0).
export function computeWoPaymentProgressDisplay({ pct, contractValue }) {
  const nilai = Number(contractValue || 0);
  if (nilai <= 0) {
    return {
      kind: "empty",
      label: "Nilai WO belum diisi",
      pctText: null,
      barPct: 0,
      barColor: "bg-slate-300",
      textColor: "text-slate-500",
    };
  }
  if (pct == null || Number.isNaN(pct)) {
    return {
      kind: "loading",
      label: "—",
      pctText: null,
      barPct: 0,
      barColor: "bg-slate-200",
      textColor: "text-slate-400",
    };
  }
  const barPct = Math.min(100, Math.max(0, pct));
  const pctText = `${pct.toFixed(1)}%`;
  if (pct > 100) {
    return {
      kind: "needs_verify",
      label: "Perlu Verifikasi",
      pctText,
      barPct: 100,
      barColor: "bg-amber-500",
      textColor: "text-amber-700",
    };
  }
  if (pct >= 100) {
    return {
      kind: "paid",
      label: pctText,
      pctText,
      barPct,
      barColor: "bg-emerald-500",
      textColor: "text-emerald-700",
    };
  }
  if (pct >= 50) {
    return {
      kind: "on_track",
      label: pctText,
      pctText,
      barPct,
      barColor: "bg-sky-500",
      textColor: "text-sky-700",
    };
  }
  return {
    kind: "in_progress",
    label: pctText,
    pctText,
    barPct,
    barColor: "bg-amber-500",
    textColor: "text-amber-700",
  };
}

// PPN (VAT) percentage — Indonesia effective 11% since April 2022. User request 10-Feb-2026.
export const PPN_PCT = 0.11;

// Display for an active project header/badge
export function projectDisplay(p) {
  if (!p) return null;
  if (p.project_type === "variation_order") {
    return {
      title: `${p.parent_project_name || p.project_name} · VO`,
      subtitle: p.variation_order_name,
      client: p.client,
      location: [p.city, p.province].filter(Boolean).join(", "),
      start: p.vo_start_date,
      end: p.vo_end_date,
      isVO: true,
    };
  }
  return {
    title: p.project_name,
    subtitle: null,
    client: p.client,
    location: [p.city, p.province].filter(Boolean).join(", "),
    start: p.start_date,
    end: p.end_date,
    isVO: false,
  };
}
